/**
 * The composer's @mention autocomplete: the open/closed query, the filtered
 * member list, the active row, and the three handlers the textarea wires up.
 * Extracted from CommentThread.tsx (refactor phase C2,
 * docs/refactoring/plans/2026-09-08-commentthread.md): the state, the filter,
 * handleTextChange and insertMention are VERBATIM; handleKeyDown is the
 * composer's former keydown branch with `return` turned into `return true`,
 * the Enter insertion reading its content and setter from the arguments, and
 * a trailing `return false`, so the caller keeps its own Enter-to-submit.
 *
 * Both handlers take the content and its setter as arguments rather than
 * closing over the composer state; the composer is their only caller since
 * 2026-09-08 (the edit box used to call handleTextChange with its own draft,
 * which opened the composer's list from the wrong textarea).
 *
 * Behaviour is pinned through the component in
 * __tests__/CommentThread.mentions.test.tsx.
 */
import { useState } from 'react';
import type React from 'react';
import type { MentionableUser } from '@overlayqa/shared';

interface UseMentionAutocompleteArgs {
  teamMembers: MentionableUser[];
  /** The composer textarea: insertion reads its caret and refocuses it. */
  textareaRef: React.RefObject<HTMLTextAreaElement>;
}

export function useMentionAutocomplete({ teamMembers, textareaRef }: UseMentionAutocompleteArgs) {
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [mentionStart, setMentionStart] = useState(-1);

  const filteredMembers =
    mentionQuery !== null
      ? teamMembers.filter((m) =>
          m.name.toLowerCase().includes(mentionQuery.toLowerCase())
        )
      : [];

  const handleTextChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
    setter: (v: string) => void
  ) => {
    const value = e.target.value;
    setter(value);
    const cursorPos = e.target.selectionStart;
    const before = value.substring(0, cursorPos);
    const atIdx = before.lastIndexOf('@');
    if (atIdx >= 0) {
      const query = before.substring(atIdx + 1);
      const charBefore = atIdx > 0 ? value[atIdx - 1] : ' ';
      if (
        (charBefore === ' ' || charBefore === '\n' || atIdx === 0) &&
        !query.includes(' ')
      ) {
        setMentionQuery(query);
        setMentionStart(atIdx);
        setActiveIndex(0);
        return;
      }
    }
    setMentionQuery(null);
  };

  const insertMention = (
    member: MentionableUser,
    currentContent: string,
    setter: (v: string) => void
  ) => {
    const before = currentContent.substring(0, mentionStart);
    const after = currentContent.substring(
      textareaRef.current?.selectionStart ?? currentContent.length
    );
    setter(`${before}@${member.name} ${after}`);
    setMentionQuery(null);
    textareaRef.current?.focus();
  };

  /**
   * The composer's keydown branch while the list is open (ArrowDown / ArrowUp
   * wrap, Enter inserts the active row, Escape closes). Returns true when it
   * consumed the key, so the caller's own Enter-to-submit only runs otherwise.
   */
  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLTextAreaElement>,
    currentContent: string,
    setter: (v: string) => void
  ): boolean => {
    if (mentionQuery !== null && filteredMembers.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((activeIndex + 1) % filteredMembers.length);
        return true;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex(
          (activeIndex - 1 + filteredMembers.length) %
            filteredMembers.length
        );
        return true;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        insertMention(filteredMembers[activeIndex], currentContent, setter);
        return true;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setMentionQuery(null);
        return true;
      }
    }
    return false;
  };

  return { mentionQuery, filteredMembers, activeIndex, handleTextChange, insertMention, handleKeyDown };
}
